/**
 * The chime set in three.js: capped tubes on cords from an elliptical iron ring, a brass
 * clapper disc and an oxblood sail below it, lit by one dim key, a red rim, and the
 * cathedral reflections from env.ts. Glow and vibration come from each tube's ring energy.
 */
import {
  AdditiveBlending, CanvasTexture, Color, Curve, CylinderGeometry, DirectionalLight, ExtrudeGeometry,
  HemisphereLight, LinearFilter, Mesh, type MeshPhysicalMaterial, type MeshStandardMaterial, Object3D, Quaternion,
  Scene, Shape, Sprite, SpriteMaterial, TubeGeometry, Vector3, type Texture, type WebGLRenderer,
} from 'three';
import type { ChimeWorld, Quality, Vec3 } from '../types';
import { v2 } from '../types';
import { damp } from '../util/math';
import { ChimeCamera } from './camera';
import { FINISHES, buildEnvironment, ironWire, shapeEmber } from './env';

export const SCENE = {
  cordRadius: 0.0022,
  ringTube: 0.0065,
  wireRadius: 0.0016,
  clapperThickness: 0.012,
  sailDepth: 0.004,
  /** metres of shimmer at energy 1 */
  jitter: 0.00055,
  /** how fast the visible glow follows the physics energy (1/s) */
  glowRate: 9,
  /**
   * energy → glow curves. Emissive rises steeply so a wind brush (0.2) is a warm cast and
   * only a full hand sweep (0.8) is clearly lit; the halo rises a little sooner so the
   * faint strikes still read at a glance.
   */
  glowGamma: 1.4,
  haloGamma: 1.0,
  /** halo width as a multiple of tube radius, length as a multiple of tube length */
  haloWidth: 7,
  haloLength: 1.1,
  handAlpha: 0.09,
} as const;

const Y = new Vector3(0, 1, 0);
const A = new Vector3(), B = new Vector3(), D = new Vector3(), ANCHOR = new Vector3();
const Q = new Quaternion(), QT = new Quaternion();

class EllipseCurve3 extends Curve<Vector3> {
  constructor(private readonly rx: number, private readonly rz: number) { super(); }
  override getPoint(t: number, target = new Vector3()): Vector3 {
    const a = t * Math.PI * 2;
    return target.set(Math.cos(a) * this.rx, 0, Math.sin(a) * this.rz);
  }
}

/** a soft capsule, white, alpha falling off from a vertical core: the struck-tube halo */
function capsuleTexture(w = 64, h = 256): CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    const ny = (y / (h - 1)) * 2 - 1;
    const dy = Math.max(0, Math.abs(ny) - 0.74) / 0.26;
    for (let x = 0; x < w; x++) {
      const nx = (x / (w - 1)) * 2 - 1;
      const d2 = nx * nx + dy * dy;
      const a = Math.exp(-d2 * 3.2) * (1 - Math.min(1, d2));
      const i = (y * w + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(255 * Math.max(0, a));
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new CanvasTexture(c);
  t.minFilter = LinearFilter; t.magFilter = LinearFilter; t.generateMipmaps = false;
  return t;
}

function discTexture(n = 64): CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, n, n);
  const t = new CanvasTexture(c);
  t.minFilter = LinearFilter; t.magFilter = LinearFilter; t.generateMipmaps = false;
  return t;
}

function leafShape(halfSize: number): Shape {
  const h = halfSize, w = halfSize * 0.56;
  const s = new Shape();
  s.moveTo(0, h);
  s.bezierCurveTo(w * 0.9, h * 0.55, w * 1.05, -h * 0.35, 0, -h);
  s.bezierCurveTo(-w * 1.05, -h * 0.35, -w * 0.9, h * 0.55, 0, h);
  s.closePath();
  return s;
}

/** position a unit Y-cylinder so it spans a → b with the given radius */
function placeSegment(o: Object3D, a: Vector3, b: Vector3, radius: number) {
  D.subVectors(b, a);
  const len = Math.max(1e-5, D.length());
  D.divideScalar(len);
  o.position.addVectors(a, b).multiplyScalar(0.5);
  o.quaternion.setFromUnitVectors(Y, D);
  o.scale.set(radius, len, radius);
}

export class ChimeScene {
  readonly scene = new Scene();
  readonly cam: ChimeCamera;
  private readonly env: Texture;
  private readonly tubes: Mesh<CylinderGeometry, MeshPhysicalMaterial>[] = [];
  private readonly cords: Mesh[] = [];
  private readonly halos: Sprite[] = [];
  private readonly glow: Float32Array;
  private readonly clapperCord: Mesh;
  private readonly clapper: Mesh;
  private readonly sailCord: Mesh;
  private readonly sail: Mesh;
  private readonly hands: Sprite[] = [];
  private readonly unitCyl: CylinderGeometry;
  private readonly wireCyl: CylinderGeometry;
  private readonly textures: Texture[] = [];
  private readonly reducedMotion: boolean;
  private readonly sTop = v2(); private readonly sBot = v2();
  /** clock for the ember creep along struck tubes (held still under reduced motion) */
  private readonly emberT = { value: 0 };

  constructor(renderer: WebGLRenderer, world: ChimeWorld, quality: Quality) {
    const lite = quality.tier === 'lite';
    this.reducedMotion = quality.reducedMotion;
    this.cam = new ChimeCamera(quality.reducedMotion);
    this.env = buildEnvironment(renderer);
    this.scene.environment = this.env;
    this.glow = new Float32Array(world.tubes.length);

    // lights: one dim key from upper-left, the red rim from lower-right
    const key = new DirectionalLight(new Color('#fff0dc'), 1.5);
    key.position.set(-2.2, 3.4, 2.6);
    const rim = new DirectionalLight(new Color('#c8323f'), 1.25);
    rim.position.set(2.6, -1.2, -1.4);
    const fill = new HemisphereLight(new Color('#161826'), new Color('#150709'), 0.25);
    this.scene.add(key, rim, fill);

    const seg = lite ? 12 : 24;
    this.unitCyl = new CylinderGeometry(1, 1, 1, seg, 1, false);
    this.wireCyl = new CylinderGeometry(1, 1, 1, lite ? 5 : 7, 1, true);
    const wire = ironWire();
    const haloTex = capsuleTexture();
    this.textures.push(haloTex);

    for (let i = 0; i < world.tubes.length; i++) {
      const spec = world.tubes[i];
      const finish = FINISHES[spec.material];
      const mesh = new Mesh(this.unitCyl, finish.make());
      mesh.material.emissive.copy(finish.glow);
      shapeEmber(mesh.material, this.emberT, i * 2.399);
      this.tubes.push(mesh);
      const cord = new Mesh(this.wireCyl, wire);
      this.cords.push(cord);
      const halo = new Sprite(new SpriteMaterial({
        map: haloTex, color: finish.glow.clone(), blending: AdditiveBlending, transparent: true,
        depthWrite: false, depthTest: false, opacity: 0,
      }));
      halo.visible = false;
      this.halos.push(halo);
      this.scene.add(mesh, cord, halo);
    }

    // the ring and its cross wires (the clapper hangs from the crossing)
    const ring = new Mesh(new TubeGeometry(new EllipseCurve3(world.ring.rx, world.ring.rz), lite ? 64 : 112, SCENE.ringTube, lite ? 6 : 10, true), FINISHES.iron.make());
    ring.position.set(world.ring.center.x, world.ring.center.y, world.ring.center.z);
    const wireX = new Mesh(this.wireCyl, wire);
    A.set(world.ring.center.x - world.ring.rx, world.ring.center.y, world.ring.center.z);
    B.set(world.ring.center.x + world.ring.rx, world.ring.center.y, world.ring.center.z);
    placeSegment(wireX, A, B, SCENE.wireRadius);
    const wireZ = new Mesh(this.wireCyl, wire);
    A.set(world.ring.center.x, world.ring.center.y, world.ring.center.z - world.ring.rz);
    B.set(world.ring.center.x, world.ring.center.y, world.ring.center.z + world.ring.rz);
    placeSegment(wireZ, A, B, SCENE.wireRadius);
    this.scene.add(ring, wireX, wireZ);

    this.clapperCord = new Mesh(this.wireCyl, wire);
    this.clapper = new Mesh(new CylinderGeometry(1, 1, 1, lite ? 24 : 40, 1, false), FINISHES.brass.make());
    this.sailCord = new Mesh(this.wireCyl, wire);
    const sailGeo = new ExtrudeGeometry(leafShape(world.sail.halfSize), {
      depth: SCENE.sailDepth, bevelEnabled: true, bevelThickness: 0.0012, bevelSize: 0.0012, bevelSegments: 2, curveSegments: lite ? 10 : 18,
    });
    sailGeo.translate(0, 0, -SCENE.sailDepth / 2);
    this.sail = new Mesh(sailGeo, FINISHES.oxblood.make());
    this.scene.add(this.clapperCord, this.clapper, this.sailCord, this.sail);

    // hands: nothing on pointer devices; a faint disc under a finger
    if (quality.touch) {
      const discTex = discTexture();
      this.textures.push(discTex);
      for (let i = 0; i < world.hands.length; i++) {
        const s = new Sprite(new SpriteMaterial({
          map: discTex, color: new Color('#c9a55a'), blending: AdditiveBlending, transparent: true,
          depthWrite: false, depthTest: false, opacity: SCENE.handAlpha,
        }));
        s.visible = false;
        this.hands.push(s);
        this.scene.add(s);
      }
    }
  }

  resize(cssW: number, cssH: number) { this.cam.resize(cssW, cssH); }

  update(world: ChimeWorld, dt: number) {
    this.cam.update(dt);
    const jitterOn = !this.reducedMotion;
    if (jitterOn) this.emberT.value += dt;

    for (let i = 0; i < this.tubes.length; i++) {
      const spec = world.tubes[i];
      const st = world.tubeStates[i];
      const mesh = this.tubes[i];
      const top: Vec3 = world.tubePoint(i, 0, A);
      const bot: Vec3 = world.tubePoint(i, 1, B);
      placeSegment(mesh, A, B, spec.radius);
      // the cord runs anchor → tube top
      ANCHOR.set(spec.anchor.x, spec.anchor.y, spec.anchor.z);
      placeSegment(this.cords[i], ANCHOR, A, SCENE.cordRadius);

      // glow: eased energy, quenched by a holding hand
      const target = st.energy * (1 - 0.92 * st.damped);
      const e = (this.glow[i] = damp(this.glow[i], target, SCENE.glowRate, dt));
      const finish = FINISHES[spec.material];
      mesh.material.emissiveIntensity = Math.pow(e, SCENE.glowGamma) * finish.glowK;
      if (jitterOn && e > 0.01) {
        const j = SCENE.jitter * e * (1 - st.damped);
        mesh.position.x += (Math.random() - 0.5) * 2 * j;
        mesh.position.z += (Math.random() - 0.5) * 2 * j;
      }

      const halo = this.halos[i];
      if (e > 0.012) {
        halo.visible = true;
        const m = halo.material;
        m.opacity = Math.pow(e, SCENE.haloGamma) * finish.haloK * (1 - st.damped);
        halo.position.addVectors(A, B).multiplyScalar(0.5);
        halo.scale.set(spec.radius * SCENE.haloWidth * (0.85 + 0.3 * e), spec.length * SCENE.haloLength, 1);
        this.cam.worldToScreen(top, this.sTop);
        this.cam.worldToScreen(bot, this.sBot);
        m.rotation = Math.atan2(this.sBot.x - this.sTop.x, this.sBot.y - this.sTop.y);
      } else {
        halo.visible = false;
      }
    }

    // clapper: cord from the ring's crossing to the disc, disc perpendicular to its cord
    const rc = world.ring.center;
    A.set(rc.x, rc.y, rc.z);
    B.set(world.clapper.pos.x, world.clapper.pos.y, world.clapper.pos.z);
    placeSegment(this.clapperCord, A, B, SCENE.cordRadius);
    D.subVectors(B, A).normalize();
    this.clapper.position.copy(B);
    this.clapper.quaternion.setFromUnitVectors(Y, D);
    this.clapper.scale.set(world.clapper.radius, SCENE.clapperThickness, world.clapper.radius);

    // sail: hangs from the disc's underside by its tip, twists a little in the wind
    const sp = world.sail.pos;
    D.set(sp.x, sp.y, sp.z);
    const up = A.subVectors(B, D).normalize(); // sail → clapper
    const tip = B.copy(D).addScaledVector(up, world.sail.halfSize);
    const under = A.copy(this.clapper.position).addScaledVector(up, -SCENE.clapperThickness * 0.5);
    placeSegment(this.sailCord, under, tip, SCENE.cordRadius * 0.85);
    this.sail.position.copy(D);
    up.subVectors(this.clapper.position, D).normalize();
    Q.setFromUnitVectors(Y, up);
    const twist = world.sail.lean.x * 1.6 + world.sail.lean.y * 0.8 + Math.sin(world.time * 0.37) * 0.12;
    QT.setFromAxisAngle(Y, twist);
    this.sail.quaternion.copy(Q).multiply(QT);

    for (let i = 0; i < this.hands.length; i++) {
      const h = world.hands[i];
      const s = this.hands[i];
      s.visible = h.active;
      if (!h.active) continue;
      s.position.set(h.pos.x, h.pos.y, h.pos.z);
      const r = h.radius * (h.pressed ? 2.6 : 2.2);
      s.scale.set(r, r, 1);
    }
  }

  dispose() {
    this.scene.traverse((o) => {
      const m = o as Mesh;
      if (m.isMesh || (o as Sprite).isSprite) {
        const mat = (o as Mesh).material as MeshStandardMaterial | SpriteMaterial;
        if (mat && typeof (mat as MeshStandardMaterial).dispose === 'function') mat.dispose();
      }
    });
    this.unitCyl.dispose();
    this.wireCyl.dispose();
    (this.sail.geometry as ExtrudeGeometry).dispose();
    this.clapper.geometry.dispose();
    for (const t of this.textures) t.dispose();
    this.env.dispose();
  }
}
